class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items') || this.closest('mini-cart-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItemDetailsButton extends HTMLElement {
  constructor() {
    super();

    this.itemNameRow = this.closest('.cart-item__name-row');
    this.button = this.querySelector('button.cart-item__name');
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();
    this.itemNameRow.classList.toggle('mobile-details-hidden');
  }
}

customElements.define('cart-item-details-button', CartItemDetailsButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus') ||
      document.getElementById('MiniCart-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const input = event.target;
    const index = input.dataset.index;
    const min = parseIntegerInput(input.dataset.min, 1);
    const max = parseIntegerInput(input.max, null);
    const step = Math.max(parseIntegerInput(input.step, 1) || 1, 1);
    const inputValue = parseIntegerInput(input.value, null);
    let message = '';

    // Empty, decimal, negative or otherwise non-numeric entries are rejected up
    // front - previously they parsed to NaN, passed every comparison below and
    // were posted to the Cart API as `"quantity": null`.
    if (inputValue === null || inputValue < 0 || inputValue < min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', min);
    } else if (max !== null && inputValue > max) {
      message = window.quickOrderListStrings.max_error.replace('[max]', max);
    } else if (inputValue % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      input.setCustomValidity('');
      input.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        input.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'final-sale-message',
        section: document.getElementById('final-sale-message').dataset.id,
        selector: '.final-sale-message'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.mini-cart__inner'
      }
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    // `line` comes from a data attribute and `quantity` from a user typed field -
    // both have to be plain integers before they can be sent to /cart/change.
    const sanitizedLine = parseIntegerInput(line, null);
    const sanitizedQuantity = parseIntegerInput(quantity, null);

    if (sanitizedLine === null || sanitizedLine < 1 || sanitizedQuantity === null || sanitizedQuantity < 0) {
      return;
    }

    this.enableLoading(line);

    const body = JSON.stringify({
      line: sanitizedLine,
      quantity: sanitizedQuantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const cartParsedState = JSON.parse(state);
        let parsedState = JSON.parse(state);
        if (typeof _lensadvizorEmbed === 'object' && typeof _lensadvizorEmbed.updateCart === 'function') {
          _lensadvizorEmbed.updateCart(cartParsedState);
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          if (typeof _lensadvizorEmbed === 'object' && typeof _lensadvizorEmbed.makeRequest === 'function') {
            const sections = this.getSectionsToRender()
              .map((section) => section.section)
              .join(',');
            const sectionData = _lensadvizorEmbed.makeRequest(
              'GET',
              routes.cart_url + '?sections=' + sections + '&sections_url=' + window.location.pathname
            );
            parsedState = { sections: sectionData, ..._lensadvizorEmbed.getCartData() };
          }

          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`) ||
            document.getElementById(`MiniCart-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');
          const cartQuantity = document.querySelector('.cart-total-product-quantity');
          const mainContent = document.querySelector('#MainContent');

          if (mainContent) mainContent.classList.toggle('main-empty-cart', parsedState.item_count === 0);
          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartQuantity) cartQuantity.innerHTML = parsedState.item_count;
          this.getSectionsToRender().forEach((section) => {
            const updatedContents = this.getSectionInnerHTMLs(parsedState.sections[section.section], section);
            // Try to find elements by selector
            let elementsToReplace = [];
            if (section.selector) {
              elementsToReplace = document.getElementById(section.id)?.querySelectorAll(section.selector);
            }

            // Fallback if no matches or no selector
            if (!elementsToReplace || elementsToReplace.length === 0) {
              const fallbackElement = document.getElementById(section.id);
              if (fallbackElement) {
                elementsToReplace = [fallbackElement];
              }
            }

            elementsToReplace.forEach((element, index) => {
              if (updatedContents[index] !== undefined) {
                element.innerHTML = updatedContents[index];
              }
            });
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`) ||
            document.getElementById(`MiniCart-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors') ||
          document.getElementById('MiniCart-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`) ||
      document.getElementById(`MiniCart-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText') ||
      document.getElementById('MiniCart-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTMLs(html, section) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // If selector is not supplied or matches nothing, fall back to section.id
    if (!section.selector) {
      const fallback = parsedHTML.getElementById(section.id);
      return fallback ? [fallback.innerHTML] : [];
    }

    const matches = parsedHTML.querySelectorAll(section.selector);
    if (matches.length > 0) {
      return Array.from(matches).map((el) => el.innerHTML);
    }

    // If no matches, fall back to section.id
    const fallback = parsedHTML.getElementById(section.id);
    return fallback ? [fallback.innerHTML] : [];
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems') ||
      document.getElementById('MiniCart-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const miniCartItemElements = this.querySelectorAll(`#MiniCart-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements, ...miniCartItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems') ||
      document.getElementById('MiniCart-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const miniCartItemElements = this.querySelectorAll(`#MiniCart-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    miniCartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            // Backstop for the textarea's maxlength - a note set programmatically
            // (autofill, extension) would otherwise reach /cart/update unbounded.
            const note = String(event.target.value ?? '').slice(0, MAX_CART_NOTE_LENGTH);
            const body = JSON.stringify({ note });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
